import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import REWARD_VAULT_ABI from 'abis/RewardVault.json'
import { REFETCH_POLICY_FAST, RefetchPolicy } from 'config/RefetchPolicies'
import { useToken, useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Farm } from 'state/farms/reducer'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { KodiakFarm } from 'types/farms'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi, Hash } from 'viem'
import { Address, encodeFunctionData } from 'viem'

import { useReadContractMultiple } from '../../../../hooks/useReadContractMultiple'
import { StakingData } from '../config'

export interface EarnedItemType {
  token: Token | null | undefined
  amount: CurrencyAmount<Token> | undefined
}

export interface TransactionState {
  hash: string
  attemptingTx: boolean
  error: string
  transactionWaiting?: boolean
}

export interface MigrationState {
  inProgress: boolean
  completed: boolean
  currentStep: number
  snapshot: {
    totalStakedAmount: string
    positions: any[]
    farmEarnings: EarnedItemType[]
  }
}

export interface RewardVaultPreferences {
  claimDestination: 'connected' | 'other'
  otherWalletAddress: string
}

export interface RewardsContextType {
  // Farm data
  kodiakFarm: Farm | null | undefined
  stakingToken: Token | null | undefined
  migrationStakingToken: Token | null | undefined
  // rewardTokens: (Token | null | undefined)[]
  kodiakFarmEarned: EarnedItemType[]
  hasKodiakFarmEarnings: boolean
  stakedPositions: any[]
  hasKodiakFarmPositions: boolean
  unlockedStaked: any[]
  hasUnlockedPositions: boolean
  totalStakedAmount: string
  willForfeitRewards: boolean

  // Vault data
  rewardVault: any | null | undefined
  rewardVaultRewardToken: Token | null | undefined
  rewardVaultEarned: EarnedItemType[]
  hasRewardVaultEarnings: boolean
  rewardVaultPreferences: RewardVaultPreferences
  updateVaultPreferences: (preferences: Partial<RewardVaultPreferences>) => void

  // UI state
  activeModal: string | null
  openModal: (modalType: string) => void
  closeModal: () => void
  stakingType: StakingData['stakingType']
  shouldShowMigration: boolean

  // User state
  account: string | undefined

  // Migration state
  migration: MigrationState
  startMigration: () => void
  completeMigration: () => void
  cancelMigration: () => void

  // Transaction handlers
  claimKodiakFarmRewards: () => Promise<string | null>
  // claimRewardVaultRewards: () => Promise<string | null>
  unstakeFromKodiakFarm: () => Promise<string | null>
  stakeToRewardVault: () => Promise<string | null>
  refreshRewardsData: () => Promise<void>
  setClaimTxState: React.Dispatch<React.SetStateAction<TransactionState>>

  // Transaction states
  claimTxState: TransactionState
  unstakeTxState: TransactionState
  stakeTxState: TransactionState
  anyTransactionInProgress: boolean

  // Approval
  approveAmount: CurrencyAmount<Token> | undefined

  // Derived state for UI
  viewType: 'migration' | 'migration-prompt' | 'rewardVault' | 'kodiakFarm' | 'empty'

  canDismissModal: boolean
}

// Context
const RewardsContext = createContext<RewardsContextType | null>(null)

// Provider props
interface RewardsProviderProps {
  children: React.ReactNode
  kodiakFarm?: KodiakFarm | null
  hasKodiakFarm?: boolean
  rewardVault?: any | null
  stakingType?: StakingData['stakingType']
  shouldShowMigration?: boolean
  refetchPolicy?: RefetchPolicy
}

// Provider component
export const RewardsProvider: React.FC<RewardsProviderProps> = ({
  children,
  kodiakFarm,
  rewardVault,
  stakingType = 'KodiakFarm' as StakingData['stakingType'],
  shouldShowMigration = false,
  refetchPolicy = REFETCH_POLICY_FAST,
}) => {
  const { account, wagmiPublicClient } = useConnection()
  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  // Contract addresses
  const kodiakFarmAddress = kodiakFarm?.id
  const rewardVaultAddress = rewardVault?.vaultAddress as Address | undefined

  // Modal state
  const [activeModal, setActiveModal] = useState<string | null>(null)

  // Transaction states
  const [claimTxState, setClaimTxState] = useState<TransactionState>({
    hash: '',
    attemptingTx: false,
    error: '',
  })

  const [unstakeTxState, setUnstakeTxState] = useState<TransactionState>({
    hash: '',
    attemptingTx: false,
    error: '',
  })

  const [stakeTxState, setStakeTxState] = useState<TransactionState>({
    hash: '',
    attemptingTx: false,
    error: '',
  })

  // Migration state
  const [migration, setMigration] = useState<MigrationState>({
    inProgress: false,
    completed: false,
    currentStep: 0,
    snapshot: {
      totalStakedAmount: '0',
      positions: [],
      farmEarnings: [],
    },
  })

  // Vault preferences
  const [rewardVaultPreferences, setRewardVaultPreferences] = useState<RewardVaultPreferences>({
    claimDestination: 'connected',
    otherWalletAddress: '',
  })

  // Determine if modal can be dismissed based on migration state
  const canDismissModal = useMemo(() => {
    return !migration.inProgress || migration.completed
  }, [migration.inProgress, migration.completed])

  const [willUseEmergency, setWillUseEmergency] = useState<boolean | null>(null)

  // Batch all KodiakFarm contract reads
  const farmContracts = useMemo(() => {
    if (!kodiakFarmAddress) return []
    const contracts: any[] = [
      {
        address: kodiakFarmAddress,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'getAllRewardTokens',
      },
      {
        address: kodiakFarmAddress,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'rewardsCollectionPaused',
      },
    ]

    if (account) {
      contracts.push(
        {
          address: kodiakFarmAddress,
          abi: KODIAK_FARM_ABI as Abi,
          functionName: 'earned',
          args: [account],
        },
        {
          address: kodiakFarmAddress,
          abi: KODIAK_FARM_ABI as Abi,
          functionName: 'lockedStakesOf',
          args: [account],
        }
      )
    }

    return contracts
  }, [kodiakFarmAddress, account])

  const { data: farmResults, refetch: refetchFarmResults } = useReadContractMultiple({
    contracts: farmContracts,
    query: {
      ...refetchPolicy,
      enabled: farmContracts.length > 0,
    },
  })

  // Extract results from batched calls
  const rawKodiakFarmRewardTokens = farmResults?.[0]?.status === 'success' ? (farmResults[0].result as string[]) : []
  const rewardCollectionPaused = farmResults?.[1]?.status === 'success' ? (farmResults[1].result as boolean) : false
  const kodiakFarmRawEarned = useMemo(
    () =>
      farmResults?.[2]?.status === 'success' && Array.isArray(farmResults[2].result)
        ? (farmResults[2].result as bigint[])
        : [],
    [farmResults]
  )
  const stakedPositionsResult = useMemo(
    () =>
      farmResults?.[3]?.status === 'success' && Array.isArray(farmResults[3].result)
        ? (farmResults[3].result as any[])
        : [],
    [farmResults]
  )

  // Batch all RewardVault contract reads
  const vaultContracts = useMemo(() => {
    if (!rewardVaultAddress) return []
    const contracts: any[] = [
      {
        address: rewardVaultAddress,
        abi: REWARD_VAULT_ABI as Abi,
        functionName: 'rewardToken',
      },
    ]

    if (account) {
      contracts.push({
        address: rewardVaultAddress,
        abi: REWARD_VAULT_ABI as Abi,
        functionName: 'earned',
        args: [account],
      })
    }

    return contracts
  }, [rewardVaultAddress, account])

  const { data: vaultResults, refetch: refetchVaultResults } = useReadContractMultiple({
    contracts: vaultContracts,
    query: {
      ...refetchPolicy,
      enabled: vaultContracts.length > 0,
    },
  })

  const refreshRewardsData = useCallback(async () => {
    await Promise.allSettled([refetchFarmResults(), refetchVaultResults()])
  }, [refetchFarmResults, refetchVaultResults])

  // Extract results from batched calls
  const rawRewardVaultRewardTokens =
    vaultResults?.[0]?.status === 'success' ? (vaultResults[0].result as string) : undefined
  const rewardVaultRawEarned = vaultResults?.[1]?.status === 'success' ? (vaultResults[1].result as bigint) : undefined

  const kodiakFarmRewardTokens = useTokens(rawKodiakFarmRewardTokens)
  const rewardVaultRewardTokens = useToken(rawRewardVaultRewardTokens)

  // Tokens
  const migrationStakingToken = useToken(kodiakFarm?.stakingToken.id)
  const stakingToken = useToken(stakingType === 'kodiakFarm' ? kodiakFarm?.stakingToken : rewardVault?.stakingToken)
  const rewardVaultRewardToken = rewardVaultRewardTokens
  const stakedPositions = useMemo(() => {
    return stakedPositionsResult || []
  }, [stakedPositionsResult])

  // Process farm earned rewards
  const kodiakFarmEarned = useMemo(() => {
    if (!kodiakFarmRawEarned || !Array.isArray(kodiakFarmRawEarned)) {
      return []
    }

    return kodiakFarmRawEarned.map((item: bigint, index: number) => ({
      token: kodiakFarmRewardTokens[index],
      amount: kodiakFarmRewardTokens[index]
        ? CurrencyAmount.fromRawAmount(kodiakFarmRewardTokens[index], item.toString())
        : undefined,
    }))
  }, [kodiakFarmRawEarned, kodiakFarmRewardTokens])

  // Process vault earned rewards
  const rewardVaultEarned = useMemo(() => {
    if (!rewardVaultRawEarned || !rewardVaultRewardToken) {
      return []
    }
    return [
      {
        token: rewardVaultRewardToken,
        amount: CurrencyAmount.fromRawAmount(rewardVaultRewardToken, rewardVaultRawEarned.toString()),
      },
    ]
  }, [rewardVaultRawEarned, rewardVaultRewardToken])

  // Check if user has farm earnings
  const hasKodiakFarmEarnings = useMemo(() => {
    if (!kodiakFarmEarned || !kodiakFarmEarned.length) return false
    return kodiakFarmEarned.some((item) => item.amount?.greaterThan('0'))
  }, [kodiakFarmEarned])

  // Check if user has vault earnings
  const hasRewardVaultEarnings = useMemo(() => {
    if (!rewardVaultEarned || !rewardVaultEarned.length) return false
    return rewardVaultEarned.some((item) => item.amount?.greaterThan('0'))
  }, [rewardVaultEarned])

  // Check if user has farm positions
  const hasKodiakFarmPositions = useMemo(() => {
    return stakedPositions && stakedPositions.length > 0
  }, [stakedPositions])

  // Get unlocked staked positions
  const unlockedStaked = useMemo(() => {
    return stakedPositions.filter(
      (position: { ending_timestamp: number }) => position.ending_timestamp <= Math.floor(Date.now() / 1000)
    )
  }, [stakedPositions])

  // Check if user has unlocked positions
  const hasUnlockedPositions = useMemo(() => {
    return unlockedStaked.length > 0
  }, [unlockedStaked])

  // Calculate total staked amount for migration
  const totalStakedAmount = useMemo(() => {
    if (!stakedPositions || !stakedPositions.length) return '0'

    return stakedPositions
      .reduce((sum: bigint, position: any) => sum + BigInt(position.liquidity?.toString() || '0'), BigInt(0))
      .toString()
  }, [stakedPositions])

  // Create CurrencyAmount for token approval
  const approveAmount = useMemo(() => {
    const amountToUse = migration.inProgress ? migration.snapshot.totalStakedAmount : totalStakedAmount

    if (!migrationStakingToken || !amountToUse || amountToUse === '0') return undefined

    try {
      return CurrencyAmount.fromRawAmount(migrationStakingToken, amountToUse)
    } catch (e) {
      console.error('Error creating CurrencyAmount for approval:', e)
      return undefined
    }
  }, [migrationStakingToken, totalStakedAmount, migration])

  // Determine view type for UI
  const viewType = useMemo(() => {
    if (migration.inProgress) {
      return 'migration'
    } else if (stakingType === 'rewardVault' && hasKodiakFarmPositions && !migration.completed) {
      return 'migration-prompt'
    } else if (stakingType === 'rewardVault' && !hasKodiakFarmPositions && hasRewardVaultEarnings) {
      return 'rewardVault'
    } else if (stakingType === 'kodiakFarm' && hasKodiakFarmEarnings) {
      return 'kodiakFarm'
    } else {
      return 'empty'
    }
  }, [
    migration.inProgress,
    migration.completed,
    stakingType,
    hasKodiakFarmPositions,
    hasRewardVaultEarnings,
    hasKodiakFarmEarnings,
  ])

  // Check if any transaction is in progress
  const anyTransactionInProgress = useMemo(() => {
    return claimTxState.attemptingTx || unstakeTxState.attemptingTx || stakeTxState.attemptingTx
  }, [claimTxState.attemptingTx, unstakeTxState.attemptingTx, stakeTxState.attemptingTx])

  // Start migration
  const startMigration = useCallback(() => {
    setMigration({
      inProgress: true,
      completed: false,
      currentStep: 0,
      snapshot: {
        totalStakedAmount,
        positions: [...stakedPositions],
        farmEarnings: [...kodiakFarmEarned],
      },
    })
    setActiveModal('migration')
  }, [totalStakedAmount, stakedPositions, kodiakFarmEarned])

  // Complete migration
  const completeMigration = useCallback(() => {
    setMigration((prev) => ({
      ...prev,
      inProgress: false,
      completed: true,
    }))

    // Reset transaction states
    setUnstakeTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
    setStakeTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
    setClaimTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
  }, [])

  // Cancel migration
  const cancelMigration = useCallback(() => {
    if (!anyTransactionInProgress) {
      setMigration({
        inProgress: false,
        completed: false,
        currentStep: 0,
        snapshot: {
          totalStakedAmount: '0',
          positions: [],
          farmEarnings: [],
        },
      })
    }
  }, [anyTransactionInProgress])

  const updateVaultPreferences = useCallback((preferences: Partial<RewardVaultPreferences>) => {
    setRewardVaultPreferences((prev) => {
      const newPrefs = { ...prev, ...preferences }

      return newPrefs
    })
  }, [])

  // Open modal
  const openModal = useCallback((modalType: string) => {
    setActiveModal(modalType)
  }, [])

  // Close modal
  const closeModal = useCallback(() => {
    if (migration.inProgress && anyTransactionInProgress) {
      return
    }

    setActiveModal(null)

    if (!migration.completed && migration.inProgress) {
      cancelMigration()
    }

    // Reset transaction states
    setUnstakeTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
    setStakeTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
    setClaimTxState({
      hash: '',
      attemptingTx: false,
      error: '',
    })
  }, [migration, anyTransactionInProgress, cancelMigration])

  // Transaction handler for claiming farm rewards
  const claimKodiakFarmRewards = useCallback(async () => {
    if (!kodiakFarmAddress || !account || !wagmiPublicClient || !kodiakFarmEarned.length) return null

    setClaimTxState({
      hash: '',
      attemptingTx: true,
      error: '',
      transactionWaiting: false,
    })

    try {
      const tx = {
        address: kodiakFarmAddress,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'getReward',
        account,
      }

      const gas = await wagmiPublicClient.estimateContractGas(tx)
      const gasLimit = calculateGasMargin(gas)

      const txHash = await writeContractAsync({ ...tx, gas: gasLimit })

      setClaimTxState({
        hash: txHash,
        attemptingTx: false,
        error: '',
        transactionWaiting: true,
      })

      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.CLAIM_FARM_REWARDS,
          rewards: kodiakFarmEarned.map((item) => ({
            token: item.token?.symbol || '',
            amount: item.amount?.toSignificant(6) || '',
          })),
        }
      )

      await waitForTransactionReceipt({ hash: txHash as Hash })

      setClaimTxState((prev) => ({
        ...prev,
        transactionWaiting: false,
      }))

      return txHash
    } catch (e: any) {
      console.error('Error claiming farm rewards:', e)

      setClaimTxState({
        hash: '',
        attemptingTx: false,
        error: e?.message || 'Failed to claim rewards',
        transactionWaiting: false,
      })

      return null
    }
  }, [
    kodiakFarmAddress,
    account,
    wagmiPublicClient,
    kodiakFarmEarned,
    addTransaction,
    writeContractAsync,
    waitForTransactionReceipt,
  ])

  // Transaction handler for unstaking from farm
  const unstakeFromKodiakFarm = useCallback(async () => {
    if (!kodiakFarmAddress || !account || !wagmiPublicClient || migration.snapshot.positions.length === 0) return null

    setUnstakeTxState({
      hash: '',
      attemptingTx: true,
      error: '',
    })

    try {
      let txHash
      const kek_ids = migration.snapshot.positions.map((position) => position.kek_id)

      if (rewardCollectionPaused) {
        const calls = kek_ids.map((kek_id) =>
          encodeFunctionData({
            abi: KODIAK_FARM_ABI as Abi,
            functionName: 'emergencyWithdraw',
            args: [kek_id],
          })
        )

        const tx = {
          address: kodiakFarmAddress,
          abi: KODIAK_FARM_ABI as Abi,
          functionName: 'multicall',
          args: [calls],
          account,
        }

        const gas = await wagmiPublicClient.estimateContractGas(tx)
        txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })
      } else {
        try {
          const tx = {
            address: kodiakFarmAddress,
            abi: KODIAK_FARM_ABI as Abi,
            functionName: 'withdrawLockedMultiple',
            args: [kek_ids],
            account,
          }

          const gas = await wagmiPublicClient.estimateContractGas(tx)
          txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })
        } catch (estimateError: any) {
          const calls = kek_ids.map((kek_id) =>
            encodeFunctionData({
              abi: KODIAK_FARM_ABI as Abi,
              functionName: 'emergencyWithdraw',
              args: [kek_id],
            })
          )

          const tx = {
            address: kodiakFarmAddress,
            abi: KODIAK_FARM_ABI as Abi,
            functionName: 'multicall',
            args: [calls],
            account,
          }

          const gas = await wagmiPublicClient.estimateContractGas(tx)
          txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })
        }
      }

      setUnstakeTxState({
        hash: txHash,
        attemptingTx: false,
        error: '',
      })

      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.UNSTAKE_LIQUIDITY_ISLAND,
          amount: '',
        }
      )

      setMigration((prev) => ({
        ...prev,
        currentStep: 1,
      }))

      return txHash
    } catch (e: any) {
      console.error('Error unstaking from farm:', e)

      setUnstakeTxState({
        hash: '',
        attemptingTx: false,
        error: e?.message || 'Failed to unstake',
      })

      return null
    }
  }, [
    kodiakFarmAddress,
    account,
    wagmiPublicClient,
    migration.snapshot.positions,
    addTransaction,
    rewardCollectionPaused,
    writeContractAsync,
  ])

  // Transaction handler for staking to vault
  const stakeToRewardVault = useCallback(async () => {
    if (!rewardVaultAddress || !account || !wagmiPublicClient) return null

    // Use the amount captured at the start of migration
    const amountToStake = migration.snapshot.totalStakedAmount

    if (!amountToStake || amountToStake === '0') return null

    setStakeTxState({
      hash: '',
      attemptingTx: true,
      error: '',
    })

    try {
      const tx = {
        address: rewardVaultAddress,
        abi: REWARD_VAULT_ABI as Abi,
        functionName: 'stake',
        args: [BigInt(amountToStake)],
        account,
      }

      const gas = await wagmiPublicClient.estimateContractGas(tx)
      const gasLimit = calculateGasMargin(gas)

      const txHash = await writeContractAsync({ ...tx, gas: gasLimit })

      setStakeTxState({
        hash: txHash,
        attemptingTx: false,
        error: '',
      })

      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.STAKE_LIQUIDITY_ISLAND,
          islandId: '',
          stakeAmount: amountToStake,
          lockTime: '',
        }
      )

      // Advance migration step
      setMigration((prev) => ({
        ...prev,
        currentStep: 2,
      }))

      return txHash
    } catch (e: any) {
      console.error('Error staking in vault:', e)

      setStakeTxState({
        hash: '',
        attemptingTx: false,
        error: e?.message || 'Failed to stake in vault',
      })

      return null
    }
  }, [
    rewardVaultAddress,
    account,
    wagmiPublicClient,
    migration.snapshot.totalStakedAmount,
    addTransaction,
    writeContractAsync,
  ])

  useEffect(() => {
    const checkEmergency = async () => {
      if (!kodiakFarmAddress || !wagmiPublicClient || !account || !migration.snapshot.positions.length) return

      if (rewardCollectionPaused) {
        setWillUseEmergency(true)
        return
      }

      try {
        const kek_ids = migration.snapshot.positions.map((p) => p.kek_id)
        await wagmiPublicClient.estimateContractGas({
          address: kodiakFarmAddress,
          abi: KODIAK_FARM_ABI as Abi,
          functionName: 'withdrawLockedMultiple',
          args: [kek_ids],
          account,
        })
        setWillUseEmergency(false)
      } catch {
        setWillUseEmergency(true)
      }
    }

    if (migration.inProgress) {
      checkEmergency()
    }
  }, [
    kodiakFarmAddress,
    wagmiPublicClient,
    account,
    migration.inProgress,
    migration.snapshot.positions,
    rewardCollectionPaused,
  ])

  const contextValue = {
    // Farm data
    kodiakFarm,
    stakingToken,
    migrationStakingToken,
    // rewardTokens,
    kodiakFarmEarned,
    hasKodiakFarmEarnings,
    stakedPositions,
    hasKodiakFarmPositions,
    unlockedStaked,
    hasUnlockedPositions,
    totalStakedAmount,
    willForfeitRewards: willUseEmergency === true,

    // Vault data
    rewardVault,
    rewardVaultRewardToken,
    rewardVaultEarned,
    hasRewardVaultEarnings,
    rewardVaultPreferences,
    updateVaultPreferences,

    // UI state
    activeModal,
    openModal,
    closeModal,
    stakingType,
    shouldShowMigration,

    // Migration state
    migration,
    startMigration,
    completeMigration,
    cancelMigration,

    // Transaction handlers
    claimKodiakFarmRewards,
    unstakeFromKodiakFarm,
    stakeToRewardVault,
    refreshRewardsData,
    setClaimTxState,

    // User state
    account,

    // Transaction states
    claimTxState,
    unstakeTxState,
    stakeTxState,
    anyTransactionInProgress,

    // Approval
    approveAmount,

    // Derived state for UI
    viewType,

    // Modal dismissal
    canDismissModal,
  }

  return <RewardsContext.Provider value={contextValue as RewardsContextType}>{children}</RewardsContext.Provider>
}

// Hook to use rewards context
export const useRewards = () => {
  const context = useContext(RewardsContext)
  if (!context) {
    throw new Error('useRewards must be used within a RewardsProvider')
  }
  return context
}

export const useOptionalRewards = () => useContext(RewardsContext)
